import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { apiGet } from '../../shared/api/httpClient'
import type { TripPoint } from '../../shared/api/types'

interface TransitPlan { source: string; departure: string | null; arrival: string | null;
  duration_minutes: number; transfers: number | null; schedule_confirmed: boolean }

export function TransitPanel({ tripId, from, to, timeZone, online }: {
  tripId: string; from: TripPoint | null; to: TripPoint | null; timeZone?: string; online: boolean
}) {
  const { t, i18n } = useTranslation()
  const query = useQuery({ queryKey: ['transit', tripId, from?.id, to?.id],
    queryFn: () => apiGet<TransitPlan>(`/trips/${tripId}/transit?from=${from!.id}&to=${to!.id}`),
    enabled: online && from?.lat != null && from.lng != null && to?.lat != null && to.lng != null,
    retry: false, staleTime: 15 * 60_000 })
  if (!from || !to) return null
  function time(value: string | null) {
    if (!value) return ''
    try { return new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit', timeZone }).format(new Date(value)) }
    catch { return value }
  }
  return <section className="mb-5 rounded-st-lg border border-st-line bg-st-surface p-4 text-sm">
    <h2 className="font-semibold">{t('transit.title')}: {from.title} → {to.title}</h2>
    {!online && <p className="text-st-muted">{t('transit.offline')}</p>}
    {online && query.isPending && <p className="text-st-muted">{t('transit.calculating')}</p>}
    {online && query.isError && <p className="text-st-muted">{t('transit.unavailable')}</p>}
    {query.data && <>
      <p className="mt-1">{t('transit.estimated_duration')}: {query.data.duration_minutes} {t('calendar.min')}</p>
      {query.data.departure && query.data.arrival && <p className="mt-1">
        {t('transit.planned_start')}: {time(query.data.departure)} → {t('transit.estimated_arrival')}: {time(query.data.arrival)}
      </p>}
      <p role="note" className="mt-2 text-xs text-st-muted">{t('transit.provisional')}</p>
      <p className="mt-1 text-xs text-st-muted">{t('transit.source')}</p>
    </>}
  </section>
}
